import { shouldNotTrack } from "@shared/services/internal_user_check_helpers";
import { HttpPromise } from "@shared/svelte/Modules/ApiService";

const extractKevelEntity = (entities: any) => {
  const kevelEntities = entities.filter((e: any) => e.type.startsWith("kevel"));

  const shoppableUnitProductEntity = kevelEntities.find(
    (e: any) => e.type === "kevel_shoppable_unit_product",
  );

  if (shoppableUnitProductEntity) {
    return shoppableUnitProductEntity;
  } else {
    return kevelEntities[0];
  }
};

export enum KevelEvents {
  AddedToCart = "addedToCart",
  Click = "click",
  ViewableInViewport = "viewableInViewport",
  OnePxInViewport = "onePxInViewport",
  BeginToRender = "beginToRender",
}

const kevelEvents = {
  click: "kevel.ad.clicked",
  beginToRender: "kevel.ad.begin_to_render",
  viewableInViewport: "kevel.ad.viewable_in_viewport",
  onePxInViewport: "kevel.ad.1px_in_viewport",
};

const sendKevelEvent = (
  event: string,
  adType: string,
  eventData,
  indexPosition: number | null = null,
) => {
  // new Date() appends a human-readable timezone, which can be too long for rails
  // en-GB locale formats the timestamp in a uniform way that rails can parse
  const sentAt = new Date().toLocaleString("en-GB", { timeZoneName: "short" });

  let forwardUrl;

  if (event === "kevel.ad.viewable_in_viewport") {
    forwardUrl = eventData.events.find(e => e.type === "ad_viewable_in_viewport")?.url;
  } else if (event === "kevel.ad.clicked") {
    forwardUrl = eventData.events.find(e => e.type === "ad_click")?.url;
  }

  let payloadData = {
    objectTrackingId: eventData.object_tracking_id,
    displayPosition: indexPosition,
    adId: eventData.ad_id,
    advertiserId: eventData.advertiser_id,
    creativeId: eventData.creative_id,
    flightId: eventData.flight_id,
    campaignId: eventData.campaign_id,
    priorityId: eventData.priority_id,
    forwardUrl: forwardUrl,
  };

  const options = {
    method: "POST",
    url: "/api/retailer/kevel_ads/track_kevel_event",
    data: {
      event: event,
      payload: payloadData,
      sentAt: sentAt,
      adType: adType,
    },
  };

  HttpPromise(options).catch(() => {});
};

const trackSponsoredProductAd = (event: string, entities: any, sponsoredProductEntity) => {
  const kevelEvent = kevelEvents[event];

  const productCardEntity = entities.find(e => e.type === "product_card");

  // indexPosition is 0-indexed, but Carrot wants us to send 1-indexed
  const indexPosition = productCardEntity.indexPosition + 1;
  sendKevelEvent(kevelEvent, "sponsored_product", sponsoredProductEntity, indexPosition);
};

const trackShoppableUnitAd = (event: string, entities: any, shoppableUnitEntity) => {
  const kevelEvent = kevelEvents[event];

  const productCardEntity = entities.find(e => e.type === "product_card");
  let position = 0;
  let entity = { ...shoppableUnitEntity };

  if (productCardEntity) {
    if (event == KevelEvents.ViewableInViewport) { return; }

    let events = entity.events;
    if (Array.isArray(events)) {
      events = events.map(eventObj => {
        if (eventObj && typeof eventObj.url === "string" && productCardEntity.productLineId) {
          const url = new URL(eventObj.url);
          url.searchParams.set("productId", productCardEntity.productLineId);
          return { ...eventObj, url: url.href };
        }
        return eventObj;
      });
      entity.events = events;
    }
    position = productCardEntity.indexPosition + 1;
  }

  sendKevelEvent(kevelEvent, "shoppable_unit", entity, position);
};

const trackBannerAd = (event: string, entities: any, bannerEntity) => {
  const kevelEvent = kevelEvents[event];

  // Banners don't have index positions like product cards, so we pass null
  sendKevelEvent(kevelEvent, "banner", bannerEntity, null);
};

export const trackKevelEvent = (event: string, entities: any) => {
  if (shouldNotTrack()) {
    return;
  }

  const kevelEntity = extractKevelEntity(entities);

  if (!kevelEntity) {
    return;
  }

  const adType = kevelEntity.type;

  switch (adType) {
    case "kevel_sponsored_product_ad":
      trackSponsoredProductAd(event, entities, kevelEntity);
      break;
    case "kevel_shoppable_unit_ad":
      trackShoppableUnitAd(event, entities, kevelEntity);
      break;
    case "kevel_banner_ad":
      trackBannerAd(event, entities, kevelEntity);
      break;
  }
};
